import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, X, MessageCircle, Bot, AlertCircle } from 'lucide-react';
import { publicAPI, widgetAPI } from '../lib/api';

const Widget = () => {
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'inline'; // 'inline' or 'bubble'
  
  const [config, setConfig] = useState(null);
  const [isOpen, setIsOpen] = useState(mode === 'inline'); // Auto-open for inline mode
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const loadConfig = useCallback(async () => {
    try {
      const data = await publicAPI.getAgent(agentId);
      setConfig({
        agent_id: data.agent_id,
        name: data.name,
        description: data.description,
        widget_settings: {
          primary_color: data.brand?.primary_color || '#6366f1',
          position: data.brand?.position || 'bottom-right',
          greeting: `Hi! I'm ${data.name}. How can I help you today?`
        }
      });
      
      // Add greeting message
      if (data.brand?.greeting) {
        setMessages([{
          role: 'assistant',
          content: data.brand.greeting
        }]);
      } else {
        setMessages([{
          role: 'assistant',
          content: `Hi! I'm ${data.name}. How can I help you today?`
        }]);
      }
    } catch (error) {
      console.error('Error loading widget config:', error);
      setError('Failed to load widget. Please check if the agent is active.');
    }
  }, [agentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setSending(true);
    setError(null);

    try {
      const response = await widgetAPI.chat(agentId, conversationId, userMessage);
      
      // Update conversation_id from response
      if (response.conversation_id && !conversationId) {
        setConversationId(response.conversation_id);
      }
      
      // Add assistant response with sources if available
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.answer,
        sources: response.sources || null
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Prompt 10: Handle specific error codes
      if (error.response?.status === 403) {
        setError('This widget is not authorized for this domain.');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, this widget is not authorized for this domain. Please contact the site administrator.' 
        }]);
      } else if (error.response?.status === 429) {
        setError('Too many requests, please try again shortly.');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Too many requests. Please wait a moment and try again.' 
        }]);
      } else {
        const errorMessage = error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.';
        setError(errorMessage);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: errorMessage 
        }]);
      }
    } finally {
      setSending(false);
    }
  };

  if (!config) {
    if (error) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Widget Error</h2>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading widget...</div>
      </div>
    );
  }

  const primaryColor = config.widget_settings?.primary_color || '#6366f1';
  const position = config.widget_settings?.position || 'bottom-right';

  // Inline mode: full page widget
  if (mode === 'inline') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ height: '600px' }}>
          {/* Header */}
          <div 
            className="p-4 flex items-center justify-between"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">{config.name}</h3>
                <p className="text-xs text-white/80">Online</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="h-[450px] overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.role === 'user' 
                      ? 'text-white' 
                      : 'bg-white border border-slate-200 text-slate-700'
                  }`}
                  style={message.role === 'user' ? { backgroundColor: primaryColor } : {}}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white">
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 border-slate-200"
                disabled={sending || !!error}
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!input.trim() || sending || !!error}
                style={{ backgroundColor: primaryColor }}
                className="hover:opacity-90"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Bubble mode: floating widget
  return (
    <div className={`fixed ${position === 'bottom-left' ? 'left-6' : 'right-6'} bottom-6 z-50`}>
      {/* Chat Window */}
      {isOpen && (
        <div 
          className="mb-4 w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-200"
          style={{ boxShadow: `0 25px 50px -12px ${primaryColor}30` }}
        >
          {/* Header */}
          <div 
            className="p-4 flex items-center justify-between"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">{config.name}</h3>
                <p className="text-xs text-white/80">Online</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="h-[350px] overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.role === 'user' 
                      ? 'text-white' 
                      : 'bg-white border border-slate-200 text-slate-700'
                  }`}
                  style={message.role === 'user' ? { backgroundColor: primaryColor } : {}}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white">
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 border-slate-200 text-sm"
                disabled={sending || !!error}
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!input.trim() || sending || !!error}
                style={{ backgroundColor: primaryColor }}
                className="hover:opacity-90 h-9 w-9"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        style={{ 
          backgroundColor: primaryColor,
          boxShadow: `0 10px 25px -5px ${primaryColor}50`
        }}
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <MessageCircle className="h-6 w-6 text-white" />
        )}
      </button>
    </div>
  );
};

export default Widget;
